import { CareProtocolApi, type CareEventInput } from "@/api/careProtocol/CareProtocolApi";
import { queryKeys } from "@/api/queryKeys";
import { useAuth } from "@/auth/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useCareReadFull(caseId: string | undefined) {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["care", "readFull", caseId ?? ""],
    queryFn: () => CareProtocolApi.canReadFull(caseId!),
    enabled: !!user?.id && !!caseId,
  });
  return { canReadFull: !!q.data, loading: q.isLoading };
}

export function useCareWrite(caseId: string | undefined) {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["care", "write", caseId ?? ""],
    queryFn: () => CareProtocolApi.canWrite(caseId!),
    enabled: !!user?.id && !!caseId,
  });
  return { canWrite: !!q.data, loading: q.isLoading };
}

export function useCareChronik(caseId: string | undefined, enabled: boolean) {
  const q = useQuery({
    queryKey: ["care", "chronik", caseId ?? ""],
    queryFn: () => CareProtocolApi.listEntries(caseId!),
    enabled: !!caseId && enabled,
  });
  return {
    entries: q.data ?? [],
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

export function usePublicCareSummary(caseId: string | undefined) {
  const q = useQuery({
    queryKey: ["care", "publicSummary", caseId ?? ""],
    queryFn: () => CareProtocolApi.getPublicSummary(caseId!),
    enabled: !!caseId,
  });
  return {
    summary: q.data ?? null,
    loading: q.isLoading,
    error: q.error,
  };
}

export function useDiagnoses(organizationId: string | undefined) {
  const q = useQuery({
    queryKey: ["care", "diagnoses", organizationId ?? ""],
    queryFn: () => CareProtocolApi.listDiagnoses(organizationId!),
    enabled: !!organizationId,
    staleTime: 60_000,
  });
  return { diagnoses: q.data ?? [], loading: q.isLoading };
}

export function useSubmitCareEntry(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      clientEntryId: string;
      recordedAt: string;
      events: CareEventInput[];
    }) =>
      CareProtocolApi.submitEntry({
        caseId: caseId!,
        clientEntryId: input.clientEntryId,
        recordedAt: input.recordedAt,
        events: input.events,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["care", "chronik", caseId ?? ""] });
      void qc.invalidateQueries({
        queryKey: ["care", "publicSummary", caseId ?? ""],
      });
      void qc.invalidateQueries({ queryKey: ["therapy", "sidebar", caseId ?? ""] });
      void qc.invalidateQueries({ queryKey: ["therapy", "dues", caseId ?? ""] });
      void qc.invalidateQueries({ queryKey: ["therapy", "reviews"] });
      void qc.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId ?? "") });
      void qc.invalidateQueries({ queryKey: queryKeys.cases.my });
    },
  });
}
